import { useCallback, useEffect, useRef } from 'react';
import { announceToScreenReader } from '../utils/accessibility';

type AnnouncementPriority = 'polite' | 'assertive';

type UseAnnouncementsResult = {
  announce: (message: string, priority?: AnnouncementPriority) => void;
  announcePolite: (message: string) => void;
  announceAssertive: (message: string) => void;
};

export function useAnnouncements(): UseAnnouncementsResult {
  // Debounce announcements to prevent spam
  const lastAnnouncement = useRef<{ message: string; time: number } | null>(null);
  const debounceTime = 500; // ms

  const announce = useCallback((message: string, priority: AnnouncementPriority = 'polite') => {
    const now = Date.now();

    // Skip if same message announced recently
    if (
      lastAnnouncement.current &&
      lastAnnouncement.current.message === message &&
      now - lastAnnouncement.current.time < debounceTime
    ) {
      return;
    }

    lastAnnouncement.current = { message, time: now };
    announceToScreenReader(message, priority);
  }, []);

  const announcePolite = useCallback((message: string) => {
    announce(message, 'polite');
  }, [announce]);

  const announceAssertive = useCallback((message: string) => {
    announce(message, 'assertive');
  }, [announce]);

  return { announce, announcePolite, announceAssertive };
}

// Common announcement messages
export const Announcements = {
  // File operations
  fileOpened: (name: string) => `Opened file ${name}`,
  fileSaved: (name: string) => `Saved file ${name}`,
  fileClosed: (name: string) => `Closed file ${name}`,
  fileCreated: (name: string) => `Created file ${name}`,
  fileDeleted: (name: string) => `Deleted file ${name}`,
  fileRenamed: (oldName: string, newName: string) => `Renamed ${oldName} to ${newName}`,

  // Search
  searchResults: (count: number, query: string) =>
    count === 0
      ? `No results found for "${query}"`
      : count === 1
      ? `1 result found for "${query}"`
      : `${count} results found for "${query}"`,
  searchMatch: (current: number, total: number) => `Match ${current} of ${total}`,

  // Git operations
  gitCommit: (message: string) => `Committed changes: ${message}`,
  gitPush: () => 'Changes pushed to remote',
  gitPull: () => 'Changes pulled from remote',
  gitBranchSwitched: (branch: string) => `Switched to branch ${branch}`,
  gitFileStaged: (name: string) => `Staged file ${name}`,
  gitFileUnstaged: (name: string) => `Unstaged file ${name}`,

  // Errors
  error: (message: string) => `Error: ${message}`,
  validationError: (message: string) => `Validation error: ${message}`,

  // Task status
  taskStarted: (task: string) => `Started ${task}`,
  taskCompleted: (task: string) => `Completed ${task}`,
  taskFailed: (task: string) => `Failed ${task}`,

  // Navigation
  navigationTo: (location: string) => `Navigated to ${location}`,
  panelFocused: (panel: string) => `${panel} panel focused`,

  // Status updates
  statusUpdate: (status: string) => status,
} as const;

// Hook for automatic status announcements
export function useStatusAnnouncements(status: string) {
  const prevStatus = useRef(status);
  const { announcePolite } = useAnnouncements();

  useEffect(() => {
    if (status && status !== prevStatus.current) {
      announcePolite(status);
      prevStatus.current = status;
    }
  }, [status, announcePolite]);
}

export default useAnnouncements;
